/**
 * The AMI008 column contract: the columns PALM expects in the outbound file, in order. Extracted
 * from willExport_item_item_ami008 so the export workflow stays readable next to the naming policy
 * in resolvePhysicalInventoryId.
 *
 * `propertyName` may be a dot path; the export rule uses it both to lay out the columns and to
 * fill in a blank for any column an item has no value for.
 *
 * `legacyPropertyName` is where the export reads a column from when `propertyName` holds
 * nothing. #1423 moved four columns off flat item properties and onto the lookup relations the
 * AMI004 import writes, and items imported before that change still hold the old paths - a live
 * inventory would otherwise export four blank columns without failing. The fallback can be
 * deleted once that data is migrated.
 */
export default [
    {
        sequence: 0,
        hNodeType: 'ShortText',
        hNodeTypeGroup: 'formElement',
        title: 'TAG_NUMBER',
        propertyName: 'assetNo'
    },
    {
        sequence: 1,
        hNodeType: 'ShortText',
        hNodeTypeGroup: 'formElement',
        title: 'SERIAL_ID',
        propertyName: 'serialNo'
    },
    {
        sequence: 2,
        hNodeType: 'ShortText',
        hNodeTypeGroup: 'formElement',
        title: 'ASSET_ID',
        propertyName: 'palmAssetId'
    },
    {
        sequence: 3,
        hNodeType: 'ShortText',
        hNodeTypeGroup: 'formElement',
        title: 'DEPTID',
        propertyName: 'organization:organization.title'
    },
    {
        sequence: 4,
        hNodeType: 'ShortText',
        hNodeTypeGroup: 'formElement',
        title: 'MODEL',
        propertyName: 'item:model.title'
    },
    {
        sequence: 5,
        hNodeType: 'ShortText',
        hNodeTypeGroup: 'formElement',
        title: 'Location',
        propertyName: 'location:location.title'
    },
    {
        sequence: 6,
        hNodeType: 'ShortText',
        hNodeTypeGroup: 'formElement',
        title: 'Area ID',
        propertyName: 'location:subLocation.title',
        legacyPropertyName: 'location:location.areaId'
    },
    {
        sequence: 7,
        hNodeType: 'ShortText',
        hNodeTypeGroup: 'formElement',
        title: 'DESCR',
        propertyName: 'description'
    },
    // `organization:person.title` has to stay the same inner property the AMI004 import writes
    // the custodian to. The import uses the tenant's organization:person lookup property, and
    // the server keeps it: dependency resolution only attaches an `_id` to a lookup object that
    // is already there, so nothing later renames the key to `title`. `title` is right on both
    // counts - PALM keys its custodian lookup on it (confirmed against the PALM use case), and
    // organization:person has no titleAlternative, so it is also what the import's own
    // passthrough entry defaults to when the item form exposes no custodian field at all. Both
    // halves are pinned by tests: the import side in willChange_import_import_PALM.unit.js, this
    // side in willExport_item_item_ami008.unit.js.
    //
    // The gap that leaves: a tenant that displays the custodian by some other property would
    // import fine and export a blank CUSTODIAN, because this path is fixed while the import's is
    // the tenant's to choose. No such tenant exists today, so it is pinned by a test rather than
    // fixed - see 'a person lookup keyed on another property writes the custodian out of AMI008
    // reach'. EMPLID and CUSTODIAN_DEPTID are not exposed this way; the import hardcodes `emplId`
    // and `department` as passthrough fields.
    {
        sequence: 8,
        hNodeType: 'ShortText',
        hNodeTypeGroup: 'formElement',
        title: 'CUSTODIAN',
        propertyName: 'organization:person.title',
        legacyPropertyName: 'custodian'
    },
    {
        sequence: 9,
        hNodeType: 'ShortText',
        hNodeTypeGroup: 'formElement',
        title: 'EMPLID',
        propertyName: 'organization:person.emplId',
        legacyPropertyName: 'custodianEmployeeID'
    },
    {
        sequence: 10,
        hNodeType: 'ShortText',
        hNodeTypeGroup: 'formElement',
        title: 'CUSTODIAN_DEPTID',
        propertyName: 'organization:person.department',
        legacyPropertyName: 'custodianDepartmentID'
    },
    {
        sequence: 11,
        hNodeType: 'ShortText',
        hNodeTypeGroup: 'formElement',
        title: 'MANUFACTURER',
        propertyName: 'item:manufacturer.title'
    },
    {
        sequence: 12,
        hNodeType: 'ShortText',
        hNodeTypeGroup: 'formElement',
        title: 'MATCH_STATUS',
        propertyName: 'matchStatus'
    },
    {
        sequence: 13,
        hNodeType: 'ShortText',
        hNodeTypeGroup: 'formElement',
        title: 'REVIEW_SW',
        propertyName: 'reviewSW'
    },

    {
        sequence: 14,
        hNodeType: 'ShortText',
        hNodeTypeGroup: 'formElement',
        title: 'IP_ADDRESS',
        propertyName: 'ipAddress'
    },
    {
        sequence: 15,
        hNodeType: 'ShortText',
        hNodeTypeGroup: 'formElement',
        title: 'IP_ALIAS',
        propertyName: 'ipAlias'
    },
    {
        sequence: 16,
        hNodeType: 'ShortText',
        hNodeTypeGroup: 'formElement',
        title: 'SOFTWARE_VERSION',
        propertyName: 'softwareVersion'
    },
    {
        sequence: 17,
        id: 'itAssetId',
        hNodeType: 'ShortText',
        hNodeTypeGroup: 'formElement',
        title: 'IT_ASSET_ID',
        propertyName: 'itAssetId'
    },
    {
        sequence: 18,
        hNodeType: 'ShortText',
        hNodeTypeGroup: 'formElement',
        title: 'SCAN_BUSINESS_UNIT',
        propertyName: 'scanBusinessUnit'
    },
    {
        sequence: 19,
        hNodeType: 'ShortText',
        hNodeTypeGroup: 'formElement',
        title: 'HAND_HELD_OPERATOR',
        propertyName: 'inventory[0].foundBy.displayName'
    },
    {
        sequence: 20,
        hNodeType: 'ShortText',
        hNodeTypeGroup: 'formElement',
        title: 'COMMENTS',
        propertyName: 'notes'
    },
    {
        sequence: 21,
        hNodeType: 'ShortText',
        hNodeTypeGroup: 'formElement',
        title: 'LAST_SCAN_DTTM',
        propertyName: 'LAST_SCAN_DTTM'
    }
];
